import { PlusIcon } from "@primer/octicons-react";
import {
    Box,
    Button,
    FormControl,
    TextInput
} from "@primer/react";
import { useEffect, useState } from "react";
import Modal from "../../components/ui/modal";
import ModalActions from "../../components/ui/modal/ModalActions";
import { IMeetingSlot } from "../../models/response/meeting/IMeetingSlot";
import { IMeetingVm } from "../../models/response/meeting/IMeetingVm";

interface Props {
    meeting: IMeetingVm;
    setMeeting: (m: IMeetingVm) => void;
    errors: any;
}

const MeetingSchedule = ({ meeting, setMeeting, errors }: Props) => {
    const [showQuickInput, setShowQuickInput] = useState(false);

    // ===== default start time từ event_time =====
    const getDefaultStartTime = () => {
        if (!meeting?.event_time) return "08:00";
        const d = new Date(meeting.event_time);
        return d.toTimeString().slice(0, 5);
    };

    const [form, setForm] = useState({
        startTime: getDefaultStartTime(),
        slotCount: 15,
        capacity: 1,
        duration: 15,
        breakTime: 5
    });

    // ===== sync khi meeting thay đổi =====
    useEffect(() => {
        if (meeting?.event_time) {
            const d = new Date(meeting.event_time);
            setForm((prev) => ({
                ...prev,
                startTime: d.toTimeString().slice(0, 5)
            }));
        }
    }, [meeting?.event_time]);

    // ===== helpers =====
    const formatTime = (date: string) => {
        const d = new Date(date);
        return d.toTimeString().slice(0, 5);
    };

    const formatRange = (start: string, duration: number) => {
        const s = new Date(start);
        const e = new Date(s.getTime() + duration * 60000);
        return `${formatTime(s.toISOString())} - ${formatTime(e.toISOString())}`;
    };

    const formatLocalISOString = (date: Date) => {
        const pad = (n: number) => String(n).padStart(2, "0");

        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
    };

    // ===== generate slots =====
    const generateSlots = (): IMeetingSlot[] => {
        const result: IMeetingSlot[] = [];

        if (!meeting?.event_time) return result;

        const baseDate = new Date(meeting.event_time);
        const [hour, minute] = form.startTime.split(":");

        baseDate.setHours(Number(hour));
        baseDate.setMinutes(Number(minute));
        baseDate.setSeconds(0);

        let current = new Date(baseDate);

        for (let i = 0; i < form.slotCount; i++) {
            result.push({
                id: -(i + 1),
                sis_meeting_id: meeting.id,
                start_at: formatLocalISOString(current),
                duration_in_minute: form.duration,
                capacity: form.capacity
            });

            current = new Date(
                current.getTime() + (form.duration + form.breakTime) * 60000
            );
        }

        return result;
    };

    const handleApplyQuickInput = () => {
        const slots = generateSlots();

        setMeeting({
            ...meeting,
            slots: slots
        });

        setShowQuickInput(false);
    };

    const removeSlot = (id: number) => {
        setMeeting({
            ...meeting,
            slots: meeting.slots.filter((x) => x.id !== id)
        });
    };

    console.log("event_time:", meeting?.event_time);
console.log("parsed:", new Date(meeting?.event_time));

    return (
        <>
            {/* ===== HEADER ===== */}
            {/* <Box sx={{ mb: 2 }}>
                <Button
                    leadingVisual={PlusIcon}
                    onClick={() => setShowQuickInput(true)}
                    disabled={!meeting?.event_time}
                >
                    Nhập nhanh khung giờ đăng ký
                </Button>
            </Box> */}

            {/* ===== GRID SLOT ===== */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(10, 1fr)",
                    gap: 2
                }}
            >
                {(meeting?.slots ?? []).map((slot) => (
                    <Box
                        key={slot.id}
                        sx={{
                            border: "1px dashed #d0d7de",
                            borderRadius: "6px",
                            p: 2,
                            position: "relative", // ⚠️ QUAN TRỌNG
                            background: "#fff",
                            fontSize: "13px",
                            overflow: "visible", // tránh bị cắt nút X
                            transition: "all 0.2s",
                            "&:hover": {
                                borderColor: "#0969da",
                                boxShadow: "0 0 0 1px #0969da20"
                            }
                        }}
                    >
                        <Box
                            onClick={() => removeSlot(slot.id)}
                            sx={{
                                position: "absolute",
                                top: "4px",
                                right: "6px",
                                width: "16px",
                                height: "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                borderRadius: "50%",
                                color: "#999",
                                cursor: "pointer",
                                zIndex: 10,
                                lineHeight: 1,
                                "&:hover": {
                                    background: "#f3f4f6",
                                    color: "red"
                                }
                            }}
                        >
                            ✕
                        </Box>

                        {/* Time */}
                        <Box sx={{ fontWeight: 600 }}>
                            {formatTime(slot.start_at)}
                        </Box>

                        {/* Range */}
                        <Box sx={{ fontSize: "12px", color: "#666" }}>
                            {formatRange(slot.start_at, slot.duration_in_minute)}
                        </Box>

                        {/* Capacity */}
                        <Box sx={{ fontSize: "12px", mt: 1 }}>
                            Sức chứa: {slot.capacity}
                        </Box>
                    </Box>
                ))}

                {/* ADD NEW */}
                <Box
                    sx={{
                        border: "1px dashed #ccc",
                        borderRadius: "6px",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                    }}
                    onClick={() => setShowQuickInput(true)}
                >
                    + Add new
                </Box>
            </Box>

            {/* ERROR */}
            {errors?.slots && (
                <FormControl>
                    <FormControl.Validation variant="error">
                        Vui lòng điền lịch họp
                    </FormControl.Validation>
                </FormControl>
            )}

            {/* ===== QUICK INPUT MODAL ===== */}
            {showQuickInput && (
                <Modal
                    title="Quick Input"
                    isOpen={true}
                    onClose={() => setShowQuickInput(false)}
                    width="large"
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 3
                        }}
                    >
                        <FormControl>
                            <FormControl.Label>Thời gian bắt đầu</FormControl.Label>
                            <TextInput
                                type="time"
                                value={form.startTime}
                                onChange={(e) =>
                                    setForm({ ...form, startTime: e.target.value })
                                }
                            />
                        </FormControl>

                        <FormControl>
                            <FormControl.Label>Số phụ huynh mỗi khung giờ</FormControl.Label>
                            <TextInput
                                type="number"
                                value={form.capacity}
                                onChange={(e) =>
                                    setForm({ ...form, capacity: +e.target.value })
                                }
                            />
                        </FormControl>

                        <FormControl>
                            <FormControl.Label>Số khung giờ</FormControl.Label>
                            <TextInput
                                type="number"
                                value={form.slotCount}
                                onChange={(e) =>
                                    setForm({ ...form, slotCount: +e.target.value })
                                }
                            />
                        </FormControl>

                        <FormControl>
                            <FormControl.Label>Thời lượng nghỉ mỗi khung giờ</FormControl.Label>
                            <TextInput
                                type="number"
                                value={form.breakTime}
                                onChange={(e) =>
                                    setForm({ ...form, breakTime: +e.target.value })
                                }
                            />
                        </FormControl>

                        <FormControl>
                            <FormControl.Label>
                                Thời lượng mỗi khung giờ (phút)
                            </FormControl.Label>
                            <TextInput
                                type="number"
                                value={form.duration}
                                onChange={(e) =>
                                    setForm({ ...form, duration: +e.target.value })
                                }
                            />
                        </FormControl>
                    </Box>

                    <ModalActions>
                        <Button onClick={() => setShowQuickInput(false)}>
                            Đóng
                        </Button>
                        <Button variant="primary" onClick={handleApplyQuickInput}>
                            Cập nhật
                        </Button>
                    </ModalActions>
                </Modal>
            )}
        </>
    );
};

export default MeetingSchedule;